import { Component, HostListener, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import dagre from '@dagrejs/dagre';
import { FtcfNode, FtcfLink } from 'src/app/model/response/ai-chat-response';
import { FtcfPointsPipe } from './ftcf-points.pipe';

const NODE_W = 220;
const NODE_H = 80;
const ZOOM_MIN = 0.3;
const ZOOM_MAX = 2;
const ZOOM_STEP = 0.1;
const ZOOM_DEFAULT = 0.65;

const NODE_TYPE_NAMES: Record<number, string> = {
  0: 'SingleVal', 1: 'FTData', 2: 'Switch', 3: 'FTDataOut',
  4: 'Math', 5: 'CyclicTmr', 6: 'Ramp', 7: 'Table',
  8: 'PID', 9: 'Action', 10: 'EMA', 11: 'Average',
  12: 'LowPass', 13: 'OutOnOff', 14: 'OutPWM', 15: 'SRLatch',
  16: 'Delay', 17: 'SampHold', 18: 'Logic', 19: 'Clamp',
  20: 'Hyster', 21: 'Counter', 22: 'DeadZone', 23: 'Deriv',
  24: 'Integ', 25: 'PeakDet', 26: 'Mux', 27: 'Tbl4x4',
  28: 'Tbl8x8', 29: 'Tbl12x12', 30: 'Vec1x4', 31: 'Vec1x8',
  32: 'Vec1x12', 33: 'Vec1x16', 34: 'OutAnalog', 35: 'ActionPerCyl',
  36: 'IgnHiResCut',
};

export interface LayoutNode extends FtcfNode {
  x: number;
  y: number;
}

export interface LayoutEdge extends FtcfLink {
  points: { x: number; y: number }[];
}

@Component({
  selector: 'app-ftcf-canvas',
  standalone: true,
  imports: [CommonModule, FtcfPointsPipe],
  templateUrl: './ftcf-canvas.component.html',
  styleUrls: ['./ftcf-canvas.component.css'],
})
export class FtcfCanvasComponent implements OnInit {
  @Input() title!: string;
  @Input() nodes!: FtcfNode[];
  @Input() links!: FtcfLink[];

  readonly NODE_W = NODE_W;
  readonly NODE_H = NODE_H;

  layoutNodes: LayoutNode[] = [];
  layoutEdges: LayoutEdge[] = [];
  svgViewBox = '0 0 0 0';
  svgWidth = 0;
  svgHeight = 0;
  zoom = ZOOM_DEFAULT;

  panX = 0;
  panY = 0;
  isPanning = false;
  private dragStartClientX = 0;
  private dragStartClientY = 0;
  private panStartX = 0;
  private panStartY = 0;

  ngOnInit(): void {
    this.buildLayout();
  }

  buildLayout(): void {
    const g = new dagre.graphlib.Graph();
    g.setGraph({ rankdir: 'LR', nodesep: 40, ranksep: 90 });
    g.setDefaultEdgeLabel(() => ({}));

    for (const node of this.nodes) {
      g.setNode(String(node.id), { width: NODE_W, height: NODE_H });
    }
    for (const link of this.links) {
      g.setEdge(String(link.fromId), String(link.toId));
    }

    dagre.layout(g);

    this.layoutNodes = this.nodes.map(node => {
      const pos = g.node(String(node.id));
      return { ...node, x: pos.x - NODE_W / 2, y: pos.y - NODE_H / 2 };
    });

    this.layoutEdges = this.links.map(link => {
      const edge = g.edge(String(link.fromId), String(link.toId));
      return { ...link, points: edge?.points ?? [] };
    });

    const maxX = Math.max(...this.layoutNodes.map(n => n.x + NODE_W), 0) + 20;
    const maxY = Math.max(...this.layoutNodes.map(n => n.y + NODE_H), 0) + 20;
    this.svgViewBox = `0 0 ${maxX} ${maxY}`;
    this.svgWidth = maxX;
    this.svgHeight = maxY;
  }

  getNodeTypeName(nodeType: number): string {
    return NODE_TYPE_NAMES[nodeType] ?? `Node${nodeType}`;
  }

  zoomIn(): void {
    this.zoom = Math.min(ZOOM_MAX, Math.round((this.zoom + ZOOM_STEP) * 100) / 100);
  }

  zoomOut(): void {
    this.zoom = Math.max(ZOOM_MIN, Math.round((this.zoom - ZOOM_STEP) * 100) / 100);
  }

  resetZoom(): void {
    this.zoom = ZOOM_DEFAULT;
  }

  onWheel(event: WheelEvent): void {
    event.preventDefault();
    if (event.deltaY < 0) {
      this.zoomIn();
    } else if (event.deltaY > 0) {
      this.zoomOut();
    }
  }

  /* Pointer, nao mouse: `mousedown` nunca chega num celular, entao o canvas
     so era navegavel pelos botoes de zoom. PointerEvent cobre mouse, toque e
     caneta com o mesmo codigo. */
  onPanStart(event: PointerEvent): void {
    this.isPanning = true;
    this.dragStartClientX = event.clientX;
    this.dragStartClientY = event.clientY;
    this.panStartX = this.panX;
    this.panStartY = this.panY;
    event.preventDefault();
  }

  @HostListener('document:pointermove', ['$event'])
  onPanMove(event: PointerEvent): void {
    if (!this.isPanning) return;
    this.panX = this.panStartX + (event.clientX - this.dragStartClientX);
    this.panY = this.panStartY + (event.clientY - this.dragStartClientY);
  }

  /* pointercancel junto com pointerup: com `touch-action: pan-y` o navegador
     assume o gesto quando ele e vertical e nos manda cancel, nunca up — sem
     isto o canvas ficava preso em modo pan depois de rolar a pagina. */
  @HostListener('document:pointerup')
  @HostListener('document:pointercancel')
  onPanEnd(): void {
    this.isPanning = false;
  }
}
